import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Skeleton,
  Alert,
  AlertTitle,
  IconButton,
  Link,
  Badge,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import PlaceIcon from '@mui/icons-material/Place';
import ApiKeyIcon from '@mui/icons-material/VpnKey';
import TuneIcon from '@mui/icons-material/Tune';
import IntegrationsIcon from '@mui/icons-material/Hub';
import AuditLogIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';
import { organizationService, Organization } from '@services/organizationService';
import { contextService, ContextUsage } from '@services/contextService';
import { getUsageThreshold, getPlanDisplayName } from '@utils/planLimits';

// Tab components
import GeneralTab from '@components/organization/GeneralTab';
import MembersTab from '@components/organization/MembersTab';
import TeamsTab from '@components/organization/TeamsTab';
import ContextsTab from '@components/organization/ContextsTab';
import OrgTestConfigSection from '@components/testconfig/OrgTestConfigSection';
import ApiKeysTab from '@components/organization/ApiKeysTab';
import AuditLogTab from '@components/organization/AuditLogTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Tab name to index mapping
// REV-488: the Invitations tab was removed — pending invitations now live in
// the Members tab.
// REV-486: 'test-config' is its own tab. It used to sit at the bottom of
// General, below the danger zone, where it read as an afterthought rather than
// the setting every tracked place inherits from.
// REV-665: 'integrations' is deliberately NOT here. It is a separate
// destination — IntegrationsPage has its own route, its own <h1> and a "Back to
// organization" control — so presenting it as a tab made the tab list lie: the
// entry carried aria-controls="org-tabpanel-5" pointing at a panel this page
// never renders, because activating it navigates away instead of revealing one.
// It is now a link beside the tab strip, which is what it always behaved like.
// REV-805: 'audit-log' is shown to every member; for anyone below admin the
// panel says the trail is for owners and admins (the API refuses them 403).
const TAB_NAMES = [
  'general',
  'members',
  'teams',
  'contexts',
  'api-keys',
  'test-config',
  'audit-log',
] as const;
type TabName = (typeof TAB_NAMES)[number];

function a11yProps(index: number) {
  return {
    id: `org-tab-${index}`,
    'aria-controls': `org-tabpanel-${index}`,
  };
}

function getTabIndex(tabName: string | null): number {
  if (!tabName) return 0;
  const index = TAB_NAMES.indexOf(tabName as TabName);
  return index >= 0 ? index : 0;
}

function OrganizationDetailPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestionCount, setSuggestionCount] = useState(0);
  const [contextUsage, setContextUsage] = useState<ContextUsage | null>(null);

  // Get active tab from URL path, default to 'general' (index 0)
  // URL format: /orgs/:slug or /orgs/:slug/:tab
  const pathParts = location.pathname.split('/');
  const tabFromPath = pathParts.length > 3 ? (pathParts[3] ?? null) : null;
  const activeTab = getTabIndex(tabFromPath);

  const fetchOrganization = useCallback(async () => {
    if (!slug) return;

    setLoading(true);
    setError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      setOrganization(org);
    } catch (err) {
      setError(t('organizations.errors.fetchFailed'));
      console.error('Failed to fetch organization:', err);
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    void fetchOrganization();
  }, [fetchOrganization]);

  const fetchContextUsage = useCallback(async () => {
    if (!organization?.id) return;
    try {
      const result = await contextService.getUsage(organization.id);
      setContextUsage(result);
    } catch {
      /* silent — don't block org page on usage fetch failure */
    }
  }, [organization?.id]);

  useEffect(() => {
    void fetchContextUsage();
  }, [fetchContextUsage]);

  // REV-939: the Tracked Places badge counts pending suggestions. It used to
  // wait for ContextsTab to report the count, which it never did, so the badge
  // read 0 on every tab. The page asks for it itself — once per visit — and
  // hands it to ContextsTab for its own notice, rather than both fetching it.
  // Nothing on this page registers or dismisses a suggestion, so there is no
  // in-page change to listen for; returning from elsewhere remounts the page
  // and refetches.
  const fetchSuggestionCount = useCallback(async () => {
    if (!organization?.id) return;
    try {
      setSuggestionCount(await contextService.getSuggestionCount(organization.id));
    } catch {
      /* silent — the badge is a hint; ContextsTab owns the suggestions UX */
    }
  }, [organization?.id]);

  useEffect(() => {
    void fetchSuggestionCount();
  }, [fetchSuggestionCount]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const tabName = TAB_NAMES[newValue];
    void navigate(`/orgs/${slug}/${tabName}`);
  };

  const handleOrganizationUpdate = (updatedOrg: Organization) => {
    setOrganization(updatedOrg);
  };

  const orgUsageThreshold = contextUsage
    ? getUsageThreshold(contextUsage.used, contextUsage.limit)
    : null;

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Skeleton variant="rectangular" height={48} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error || !organization) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('organizations.errors.fetchFailed')}
        </Alert>
        <Link component={RouterLink} to="/orgs" sx={{ display: 'flex', alignItems: 'center' }}>
          <ArrowBackIcon sx={{ mr: 1 }} />
          {t('navigation.organizations')}
        </Link>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs are rendered globally by MainLayout (REV-399 removed the
          duplicate page-local trail). The global breadcrumb shows the org name
          for the slug segment via the active-org store. */}

      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <IconButton
          component={RouterLink}
          to="/orgs"
          sx={{ mr: 2 }}
          aria-label={t('navigation.organizations')}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {organization.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            /{organization.slug}
          </Typography>
        </Box>
      </Box>

      {/* Context limit alerts */}
      {orgUsageThreshold?.severity === 'at' && contextUsage && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Link href={t('trackedPlaces.limits.upgradeUrl')}>
              {t('trackedPlaces.limits.upgrade')}
            </Link>
          }
        >
          <AlertTitle>{t('trackedPlaces.limits.atLimit')}</AlertTitle>
          {t('trackedPlaces.limits.atLimitDetail', {
            used: contextUsage.used,
            limit: contextUsage.limit,
            plan: getPlanDisplayName(contextUsage.plan),
          })}
        </Alert>
      )}
      {orgUsageThreshold?.severity === 'near' && contextUsage && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Link href={t('trackedPlaces.limits.upgradeUrl')}>
              {t('trackedPlaces.limits.upgrade')}
            </Link>
          }
        >
          <AlertTitle>{t('trackedPlaces.limits.nearLimit')}</AlertTitle>
          {t('trackedPlaces.limits.nearLimitDetail', {
            used: contextUsage.used,
            limit: contextUsage.limit,
            plan: getPlanDisplayName(contextUsage.plan),
          })}
        </Alert>
      )}

      {/* Tabs */}
      <Box
        sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'flex-end' }}
      >
        <Tabs
          // Scrollable because the strip already overflowed at common widths —
          // the baseline clipped "Test configuration" behind an overflow
          // indicator, leaving a tab that could be focused but not read.
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flexGrow: 1, minWidth: 0 }}
          value={activeTab}
          onChange={handleTabChange}
          aria-label={t('organizations.settings.title')}
        >
          <Tab
            icon={<SettingsIcon />}
            iconPosition="start"
            label={t('organizations.settings.general')}
            {...a11yProps(0)}
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label={t('organizations.settings.members')}
            {...a11yProps(1)}
          />
          <Tab
            icon={<GroupsIcon />}
            iconPosition="start"
            label={t('organizations.settings.teams')}
            {...a11yProps(2)}
          />
          <Tab
            icon={<PlaceIcon />}
            iconPosition="start"
            label={
              // REV-939: with nothing pending there is no badge at all — MUI
              // keeps a hidden zero badge's text in the DOM, which named the
              // tab "Tracked Places0". When there is a count, the bare number is
              // hidden from assistive tech and said in words instead.
              suggestionCount > 0 ? (
                <Badge
                  badgeContent={suggestionCount}
                  color="info"
                  max={99}
                  slotProps={{ badge: { 'aria-hidden': true } }}
                >
                  {t('organizations.settings.trackedPlaces')}
                  <Box component="span" sx={visuallyHidden}>
                    {', '}
                    {suggestionCount === 1
                      ? t('trackedPlaces.suggestions.pendingCount', { count: suggestionCount })
                      : t('trackedPlaces.suggestions.pendingCountPlural', {
                          count: suggestionCount,
                        })}
                  </Box>
                </Badge>
              ) : (
                t('organizations.settings.trackedPlaces')
              )
            }
            {...a11yProps(3)}
          />
          <Tab
            icon={<ApiKeyIcon />}
            iconPosition="start"
            label={t('organizations.settings.apiKeys', 'API Keys')}
            {...a11yProps(4)}
          />
          <Tab
            icon={<TuneIcon />}
            iconPosition="start"
            label={t('organizations.settings.testConfig', 'Test configuration')}
            {...a11yProps(5)}
          />
          <Tab
            icon={<AuditLogIcon />}
            iconPosition="start"
            label={t('organizations.settings.auditLog')}
            {...a11yProps(6)}
          />
        </Tabs>
        {/* A link, not a tab: it goes to its own page rather than revealing a
            panel here. Kept adjacent to the strip so it stays as easy to find
            as it was when it sat inside it — this is the only route to
            Integrations in the UI. */}
        <Button
          component={RouterLink}
          to={`/orgs/${slug}/integrations`}
          startIcon={<IntegrationsIcon />}
          // REV-691: aligned to the tab strip's BASELINE rather than centred
          // against it. Measured: with alignSelf 'center' this link's bottom
          // edge sat 16px above the strip's, which is what the walkthrough
          // meant by "not in line with these other options"; flex-end plus a
          // half-step of margin brings it to 4px.
          //
          // NOT caused by flexGrow on the Tabs, which was my first theory and
          // is measurably false - the gap between strip and link is 8px with
          // or without it, at 900px and at 1600px.
          sx={{
            flexShrink: 0,
            alignSelf: 'flex-end',
            ml: 2,
            mb: 0.5,
          }}
        >
          {t('organizations.settings.integrations', 'Integrations')}
        </Button>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <GeneralTab organization={organization} onUpdate={handleOrganizationUpdate} />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <MembersTab orgId={organization.id} />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <TeamsTab orgId={organization.id} orgSlug={organization.slug} />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <ContextsTab
          orgId={organization.id}
          orgSlug={organization.slug}
          suggestionCount={suggestionCount}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <ApiKeysTab orgId={organization.id} />
      </TabPanel>
      <TabPanel value={activeTab} index={5}>
        <OrgTestConfigSection orgId={organization.id} />
      </TabPanel>
      <TabPanel value={activeTab} index={6}>
        <AuditLogTab orgId={organization.id} />
      </TabPanel>
    </Box>
  );
}

export default OrganizationDetailPage;
